import { Product, ProductVariant } from '../types';
import { getStoreByOwner } from '../services/stores.service';
import { getProductStock } from './stockUtils';
import { findVariantByAttributes as findVariant, getAvailableStock } from './variant';
import { globalRateLimiter } from './rateLimiting';

// Centralized price calculation utilities

/**
 * Centralized price calculation function
 * Always returns the discounted price (effective price) for a product
 * 
 * Priority order:
 * 1. Variant price (if variable product with variant selected)
 * 2. Product discountPrice (if valid number > 0)
 * 3. Product price (fallback)
 * 
 * @param product - The product object
 * @param selectedVariation - Optional selected variation (size, color) for variable products
 * @returns The effective/discounted price to display
 */
export const getProductPrice = (
  product: Product,
  selectedVariation?: { size?: string; color?: string } | Record<string, string>
): number => {
  if (!product) return 0;

  // For variable products, check variant price first
  if (product.productType === 'variable' && selectedVariation && product.variants?.length) {
    const selected: Record<string, string> = {};
    
    // Handle both object formats: { size, color } or { Size, Color }
    if ('size' in selectedVariation && selectedVariation.size) {
      selected['Size'] = selectedVariation.size;
    } else if ('Size' in selectedVariation) {
      selected['Size'] = selectedVariation.Size;
    }
    
    if ('color' in selectedVariation && selectedVariation.color) {
      selected['Color'] = selectedVariation.color;
    } else if ('Color' in selectedVariation) {
      selected['Color'] = selectedVariation.Color;
    }

    const variant = findVariant(product, selected);
    
    if (variant) {
      // Check variant price (server uses v.price, client uses priceOverride)
      const variantPrice = (variant as any).price ?? variant.priceOverride;
      
      if (typeof variantPrice === 'number' && variantPrice >= 0) {
        // CRITICAL FIX: If product has discountPrice, only use variant price if it's a variant-specific discount
        // (different from both original price and discountPrice)
        if (typeof product.discountPrice === 'number' && product.discountPrice > 0) {
          if (variantPrice !== product.price && variantPrice !== product.discountPrice) {
            return variantPrice; // Variant has unique discounted price
          } else {
            // Use product discountPrice (variant price matches original)
            // TypeScript knows discountPrice is number here due to the typeof check above
            return product.discountPrice;
          }
        } else {
          // No product discountPrice, use variant price
          return variantPrice;
        }
      }
      // If variant exists but has no price, fall through to product discountPrice
    }
  }

  // For simple products or when no variant price found:
  // Prioritize discountPrice if it's a valid number > 0
  if (typeof product.discountPrice === 'number' && product.discountPrice > 0) {
    return product.discountPrice;
  }

  // Fallback to regular price
  return product.price ?? 0;
};

/**
 * Get the original/MRP price (before discount)
 * @param product - The product object
 * @returns The original price (MRP)
 */
export const getProductMRP = (product: Product): number => {
  if (!product) return 0;
  return product.price ?? 0;
};

// Get simple product price (base and effective)
// @deprecated Use getProductPrice and getProductMRP instead
export const getSimplePrice = (product: Product) => {
  const base = product.price;
  const effective = getProductPrice(product);
  return { base, effective };
};

// Get variable product price range (min and max)
export const getVariablePriceRange = (product: Product) => {
  if (!product.variants || product.variants.length === 0) {
    const price = getProductPrice(product);
    return { min: price, max: price };
  }

  const prices = product.variants.map(variant => {
    const variantPrice = (variant as any).price ?? variant.priceOverride;
    if (typeof variantPrice === 'number' && variantPrice >= 0) {
      return variantPrice;
    }
    return getProductPrice(product);
  });

  return {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
};

// Get discount percentage for simple products
export const getDiscountPercent = (product: Product) => {
  if (product.productType === 'variable' || !product.discountPrice) {
    return 0;
  }
  
  const discount = product.price - product.discountPrice;
  return Math.round((discount / product.price) * 100);
};

// Get total stock for product
// @deprecated Use getProductStock from stockUtils.ts instead
// This function is kept for backward compatibility but delegates to getProductStock
export const getTotalStock = (product: Product) => {
  return getProductStock(product);
};

// Find variant by selected attributes
// @deprecated Use findVariantByAttributes from variant.ts instead
// This function is kept for backward compatibility but delegates to variant.ts
export const findVariantByAttributes = (product: Product, selected: Record<string, string>) => {
  return findVariant(product, selected);
};

/**
 * Get selected variant price (deprecated - use getProductPrice instead)
 * @deprecated Use getProductPrice instead for consistency
 */
export const getSelectedVariantPrice = (product: Product, selected: Record<string, string>) => {
  return getProductPrice(product, selected);
};

// Get selected variant stock
export const getSelectedStock = (product: Product, selected: Record<string, string>) => {
  const variant = product.productType === 'variable' 
    ? findVariantByAttributes(product, selected)
    : null;
  return getAvailableStock(product, variant);
};

// Clean and fix product titles
export const cleanProductTitle = (title: string): string => {
  if (!title) return 'Untitled Product';
  
  let cleaned = title.trim();
  
  // Fix common grammar issues
  cleaned = cleaned.replace(/\bfor\s+(\w+)\s+childs\b/gi, 'for $1 children');
  cleaned = cleaned.replace(/\bchilds\b/gi, 'children');
  cleaned = cleaned.replace(/\bmens\b/gi, "men's");
  cleaned = cleaned.replace(/\bwomens\b/gi, "women's");
  cleaned = cleaned.replace(/\bboys\b/gi, 'boys');
  cleaned = cleaned.replace(/\bgirls\b/gi, 'girls');
  
  // Fix awkward spacing and punctuation
  cleaned = cleaned.replace(/\s+/g, ' '); // Multiple spaces to single space
  cleaned = cleaned.replace(/\s*,\s*/g, ', '); // Fix comma spacing
  cleaned = cleaned.replace(/\s*\.\s*/g, '. '); // Fix period spacing
  
  // Capitalize first letter of each word for better readability
  cleaned = cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
  
  // Fix specific awkward phrases
  cleaned = cleaned.replace(/\bKta\s+Manxe\s+Laai\b/gi, 'for Men');
  cleaned = cleaned.replace(/\bBor\s+Han\b/gi, 'Boys');
  cleaned = cleaned.replace(/\bLimited\s+Stock\b/gi, 'Limited Stock');
  
  return cleaned;
};

// Validate and fix product data structure
export const validateAndFixProduct = (product: Product): Product => {
  if (!product) {
    throw new Error('Product is required');
  }

  const fixedProduct = { ...product };

  // Ensure product has required fields
  if (!fixedProduct.id) {
    throw new Error('Product ID is required');
  }

  if (!fixedProduct.title) {
    fixedProduct.title = 'Untitled Product';
  } else {
    // Clean the product title
    fixedProduct.title = cleanProductTitle(fixedProduct.title);
  }

  if (!fixedProduct.price || fixedProduct.price <= 0) {
    fixedProduct.price = 0;
  }

  // Fix variants structure
  if (fixedProduct.variants && Array.isArray(fixedProduct.variants)) {
    fixedProduct.variants = fixedProduct.variants.map((variant, index) => {
      const fixedVariant = { ...variant };

      // Ensure variant has required structure
      if (!fixedVariant.attributeValues) {
        fixedVariant.attributeValues = {};
      }

      // Ensure Size and Color attributes exist
      if (!fixedVariant.attributeValues.Size) {
        fixedVariant.attributeValues.Size = 'One Size';
      }
      if (!fixedVariant.attributeValues.Color) {
        fixedVariant.attributeValues.Color = 'Default';
      }

      // Ensure stock quantity is valid
      if (typeof fixedVariant.stockQty !== 'number' || fixedVariant.stockQty < 0) {
        fixedVariant.stockQty = 0;
      }

      // Ensure SKU exists
      if (!fixedVariant.sku) {
        fixedVariant.sku = `${fixedProduct.id}_${fixedVariant.attributeValues.Size}_${fixedVariant.attributeValues.Color}`;
      }

      // Ensure variant is active by default
      if (fixedVariant.isActive === undefined) {
        fixedVariant.isActive = true;
      }

      return fixedVariant;
    });

    // Calculate total stock from variants
    fixedProduct.totalStock = fixedProduct.variants.reduce((sum, variant) => sum + (variant.stockQty || 0), 0);
  } else {
    // If no variants, create a default variant for simple products
    fixedProduct.variants = [{
      attributeValues: {
        Size: 'One Size',
        Color: 'Default'
      },
      stockQty: fixedProduct.totalStock || fixedProduct.stockQty || 0,
      sku: `${fixedProduct.id}_OneSize_Default`,
      isActive: true,
      soldQuantity: fixedProduct.soldQuantity || 0,
      barcode: null,
      image: fixedProduct.images?.[0] || '',
      priceOverride: null
    }];
    fixedProduct.totalStock = fixedProduct.variants[0]?.stockQty ?? 0;
  }

  // Ensure product type is set
  if (!fixedProduct.productType) {
    fixedProduct.productType = (fixedProduct.variants?.length ?? 0) > 1 ? 'variable' : 'simple';
  }

  // Ensure status is set
  if (!fixedProduct.status) {
    fixedProduct.status = 'approved';
  }

  return fixedProduct;
};

// Check if product has valid variants
export const hasValidVariants = (product: Product): boolean => {
  if (!product?.variants || !Array.isArray(product.variants)) {
    return false;
  }

  return product.variants.some(variant => {
    return (
      variant.attributeValues?.Size &&
      variant.attributeValues?.Color &&
      typeof variant.stockQty === 'number' &&
      variant.stockQty >= 0 &&
      variant.isActive !== false
    );
  });
};

// Get available variants with stock
export const getAvailableVariants = (product: Product): ProductVariant[] => {
  if (!hasValidVariants(product)) {
    return [];
  }

  return (product.variants ?? []).filter(variant => 
    (variant.stockQty ?? 0) > 0 && variant.isActive !== false
  );
};

// Check if product can be added to cart
export const canAddToCart = (product: Product, selectedSize?: string, selectedColor?: string, quantity: number = 1): { canAdd: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!product) {
    errors.push('Product not found');
    return { canAdd: false, errors };
  }

  if (!hasValidVariants(product)) {
    errors.push('Product has no valid variants');
    return { canAdd: false, errors };
  }

  // For products with variants, size and color are required
  if ((product.variants?.length ?? 0) > 1) {
    if (!selectedSize) {
      errors.push('Please select a size');
    }
    if (!selectedColor) {
      errors.push('Please select a color');
    }
  }

  // Find the selected variant
  const selectedVariant = (product.variants ?? []).find(variant => {
    const variantSize = variant.attributeValues?.Size || 'One Size';
    const variantColor = variant.attributeValues?.Color || 'Default';
    return variantSize === (selectedSize || 'One Size') && variantColor === (selectedColor || 'Default');
  });

  if (!selectedVariant) {
    errors.push('Selected variant not found');
    return { canAdd: false, errors };
  }

  if ((selectedVariant?.stockQty ?? 0) <= 0) {
    errors.push('Selected variant is out of stock');
    return { canAdd: false, errors };
  }

  if (quantity > (selectedVariant?.stockQty ?? 0)) {
    errors.push(`Only ${selectedVariant?.stockQty ?? 0} items available`);
    return { canAdd: false, errors };
  }

  return { canAdd: true, errors };
};

// Store Navigation Utilities

/**
 * Navigate to a store page with proper fallback logic
 * @param sellerId - The seller/user ID
 * @param navigate - React Router navigate function
 * @param useSlug - Whether to prefer slug-based URLs (default: true)
 * @param routeType - Which route pattern to use: 'store' or 'store-page' (default: 'store-page')
 */
export async function navigateToStore(
  sellerId: string, 
  navigate: (path: string) => void,
  useSlug: boolean = true,
  routeType: 'store' | 'store-page' = 'store-page'
): Promise<void> {
  if (!sellerId) {
    // Removed debug log to prevent sensitive data exposure
    return;
  }

  // SECURITY: Rate limiting to prevent excessive network calls
  const rateLimit = globalRateLimiter.isAllowed('navigateToStore', 'productUtils');
  if (!rateLimit.allowed) {
    // Rate limit exceeded - fallback to direct navigation without slug lookup
    navigate(`/${routeType}/${sellerId}`);
    return;
  }

  try {
    if (useSlug) {
      // First try to find a store with slug for this seller
      const store = await getStoreByOwner(sellerId);
      
      if (store && store.slug) {
        // Use the store slug for better SEO
        navigate(`/${routeType}/${store.slug}`);
        return;
      }
    }
    
    // Fallback to seller ID
    navigate(`/${routeType}/${sellerId}`);
  } catch (error) {
    // Removed debug log to prevent sensitive data exposure
    // Fallback to seller ID if store lookup fails
    navigate(`/${routeType}/${sellerId}`);
  }
}

/**
 * Get the best store URL for a seller
 * @param sellerId - The seller/user ID
 * @param routeType - Which route pattern to use: 'store' or 'store-page' (default: 'store-page')
 * @returns Promise<string> - The best URL to use for the store
 */
export async function getStoreUrl(sellerId: string, routeType: 'store' | 'store-page' = 'store-page'): Promise<string> {
  if (!sellerId) {
    return '/stores'; // Fallback to stores directory
  }

  // SECURITY: Rate limiting to prevent excessive network calls
  const rateLimit = globalRateLimiter.isAllowed('getStoreUrl', 'productUtils');
  if (!rateLimit.allowed) {
    // Rate limit exceeded - return direct URL without slug lookup
    return `/${routeType}/${sellerId}`;
  }

  try {
    const store = await getStoreByOwner(sellerId);
    
    if (store && store.slug) {
      return `/${routeType}/${store.slug}`;
    }
    
    return `/${routeType}/${sellerId}`;
  } catch (error) {
    // Removed debug log to prevent sensitive data exposure
    return `/${routeType}/${sellerId}`;
  }
}

